import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard, FileText, Building2, BookOpen, Calculator,
  MessageSquare, FileDown, LogOut, User, Menu, X,
  ChevronDown, FileCheck, FilePlus2, Activity, TrendingUp, Upload, Shield,
  Handshake, BarChart3, Wallet, Users, Target
} from 'lucide-react';
import SystemIntegrityBadge from './SystemIntegrityBadge';

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/pnl', label: 'P&L', icon: TrendingUp },
  { path: '/targets', label: 'Targets', icon: Target },
  { path: '/obligations', label: 'Obligations', icon: Wallet },
  { path: '/payment-commands', label: 'Payment Commands', icon: Wallet },
  { path: '/sales-capture', label: 'Sales Source', icon: Upload },
  { path: '/bills', label: 'Bills', icon: FileText },
  { path: '/credit-debit-notes', label: 'CN/DN', icon: FilePlus2 },
  { path: '/vendors', label: 'Vendors', icon: Building2 },
  { path: '/partners', label: 'Partners', icon: Handshake },
  { path: '/transactions', label: 'Transactions', icon: BarChart3 },
  { path: '/cash', label: 'Cash Position', icon: Wallet },
  { path: '/cash-flow', label: 'Cash Flow', icon: BookOpen },
  { path: '/payroll', label: 'Payroll Payouts', icon: Users },
  { path: '/service-charge', label: 'Service Charge', icon: Users },
  { path: '/tax', label: 'Tax Dashboard', icon: Calculator },
  { path: '/gstr2b', label: 'GSTR-2B Recon', icon: FileCheck },
  { path: '/audit', label: 'Audit Log', icon: Activity },
  { path: '/deletion-requests', label: 'Deletion Requests', icon: Shield },
  { path: '/chat', label: 'AI Analytics', icon: MessageSquare },
  { path: '/exports', label: 'CA Exports', icon: FileDown },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleColors = {
    OWNER: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    FINANCE: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    CA: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    AUDITOR: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    VIEWER: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border/50 bg-card/30 backdrop-blur-sm">
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">1</span>
            </div>
            <span className="font-manrope font-bold text-lg">OS Accounts</span>
          </div>
          <SystemIntegrityBadge />
        </div>

        {/* Organization Display */}
        <div className="p-4 border-b border-border/50">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background/50 border border-border/50">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium truncate" data-testid="org-name">
              {user?.org_name || 'Organization'}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  )}
                  data-testid={`nav-${item.path.slice(1) || 'dashboard'}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User */}
        <div className="p-4 border-t border-border/50">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center gap-2">
                  <span>Role:</span>
                  <Badge variant="outline" className={roleColors[user?.role]}>
                    {user?.role}
                  </Badge>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-rose-400">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card/80 backdrop-blur-xl border-b border-border/50 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
            <span className="text-white font-bold text-sm">1</span>
          </div>
          <span className="font-manrope font-bold">OS Accounts</span>
        </div>
        <div className="flex items-center gap-2">
          <SystemIntegrityBadge />
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background/95 backdrop-blur-xl z-30 p-4">
          <nav className="space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-6 pt-6 border-t border-border/50">
            <Button variant="ghost" className="w-full justify-start text-rose-400" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="lg:hidden h-16" /> {/* Spacer for mobile header */}
        <div className="max-w-[1600px] mx-auto p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
